import React, { useState, useEffect, useRef } from "react";
import { Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { get, post } from "util/requests";
import { DataGrid } from "@mui/x-data-grid";
import styles from "../corelab.module.css";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import BreadCrumbs from "../components/bread_crumbs";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AddIcon from "@mui/icons-material/Add";
import NewFolderModal from "../components/new_folder_modal";
import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import SelectedFileButtons from "ui/buttons/selected_file_buttons";
import moment from "moment";
import { handleFileAddedToQueue } from "ui/upload_status/helpers";
import DeleteAssetModal from "../components/delete_asset_modal";
import ContainsDataNoDeleteModal from "../components/contains_data_no_delete_modal";
import { arrayHaveCommonItems } from "util/helpers";
import ReplaceModal from "../components/replace_modal";
import RenameModal from "../components/rename_modal";
import InvalidExtensionModal from "../components/invalid_extension_modal";
import FlagsModal from "../components/flags_modal";
import { ThemeProvider } from "@mui/material";
import { theme, corelab_api_url } from "config";
import colors from "colors";
import Tag from "ui/general/tag";
import FileFolderToggle from "ui/general/file_folder_toggle";
import { CheckIcon } from "ui/icons";

export default function FileUploadUI(props) {
  const { from_module_bar = null, subject_id = null, toggleViewMode, viewMode, parent } = props;

  const [data, setData] = useState(null);
  const [location, setLocation] = useState([]);
  const [addFolderIsOpen, setAddFolderIsOpen] = useState(false);
  const [deleteAssetIsOpen, setDeleteAssetIsOpen] = useState(false);
  const [containsDataIsOpen, setContainsDataIsOpen] = useState(false);
  const [replaceModalIsOpen, setReplaceModalIsOpen] = useState(false);
  const [renameModalIsOpen, setRenameModalIsOpen] = useState(false);
  const [showFlagsModal, setShowFlagsModal] = useState(false);

  const [invalidExtensionModalIsOpen, setInvalidExtensionModalIsOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const file_select_ref = useRef(null);
  const dispatch = useDispatch();
  const refresh_file_list = useSelector((state) => state.files.refresh_file_list);
  const file_queue = useSelector((state) => state.files.files);

  const study_id = useSelector((state) => state.study.active_study_id);

  const get_current_location_uuid = () => location.slice(-1)[0]?.uuid;

  const fetch_file_list = () => {
    setData(null);
    get(`${corelab_api_url}/file_data`, {
      params: {
        subject_id,
        study_id,
        uuid: get_current_location_uuid() || null,
      },
    }).then((ret) => {
      let loc = [
        {
          name: ret.data.root?.name,
          uuid: ret.data.root?.uuid,
          type: "parent",
          clickable: from_module_bar,
        },
      ];
      if (ret.data.root && ret.data.folders) {
        ret.data.folders.forEach((folder) => {
          loc.push({
            name: folder.name,
            uuid: folder.uuid,
            type: "folder",
            clickable: folder.type === "subject_id",
          });
        });
        setLocation(loc);
        setData(ret.data.rows || []);
      } else if (get_current_location_uuid() && ret.data) {
        setData(ret.data || []);
      }
    });
  };

  useEffect(() => {
    fetch_file_list();
  }, [refresh_file_list, subject_id]);

  const fetch_path = (obj, direction = "down") => {
    let _location;
    if (direction === "down") {
      _location = location;
      _location.push({ ...obj, clickable: true });
    } else {
      let index = location.findIndex((crumb) => crumb.uuid === obj.uuid);
      if (index === 0 || index === -1) {
        _location = location.slice(0, 1);
      } else {
        _location = location.slice(0, index + 1);
      }
    }

    setLocation(_location);
    setSelectedFiles([]);
    get(`${corelab_api_url}/file_data`, {
      params: { uuid: obj.uuid, study_id },
    }).then((ret) => {
      if (ret.data.root && ret.data.folders) {
        setData(ret.data.rows || []);
      } else if (get_current_location_uuid() && ret.data) {
        setData(ret.data || []);
      }

      // setData(ret.data);
      setSelectedFiles([]);
    });
  };

  const createNewFolder = (folder_name) => {
    let uuid = location.slice(-1)[0].uuid;
    post(`${corelab_api_url}/folder`, { uuid, folder_name, subject_id }).then((ret) => {
      let new_folder = ret.data;
      fetch_file_list();
      setAddFolderIsOpen(false);
    });
  };

  const toggleFileSelection = (uuid, type, name, aws_key, flags) => {
    let is_selected = selectedFiles.map((sf) => sf.uuid).includes(uuid);
    let _selectedFiles = [...selectedFiles];
    if (is_selected) {
      _selectedFiles = _selectedFiles.filter((sf) => sf.uuid !== uuid);
    } else {
      _selectedFiles.push({ uuid, type, name, aws_key, flags });
    }
    setSelectedFiles(_selectedFiles);
  };

  const deleteAssets = () => {
    post(`${corelab_api_url}/delete_assets`, {
      list: selectedFiles,
      parent_uuid: get_current_location_uuid(),
    }).then(() => {
      setDeleteAssetIsOpen(false);
      fetch_file_list();
      setSelectedFiles([]);
    });
  };

  const initDelete = () => {
    post(`${corelab_api_url}/check_can_delete`, {
      list: selectedFiles,
      parent_uuid: get_current_location_uuid(),
    }).then((ret) => {
      if (ret.data.can_delete) {
        setDeleteAssetIsOpen(true);
      } else {
        setContainsDataIsOpen(true);
      }
    });
  };
  const handleModifyFlags = (uuid, flags) => {
    return new Promise((resolve) => {
      post(`${corelab_api_url}/file_flags/${uuid}`, { flags }).then(() => {
        let file = selectedFiles.find((f) => f.uuid === uuid);
        file.flags = flags;
        setSelectedFiles([...selectedFiles]);
        let _data = [...data];
        file = _data.find((f) => f.uuid === uuid);
        file.flags = flags;
        setData([..._data]);
        resolve();
      });
    });
  };

  let columns = [
    {
      field: "selected",
      headerName: "",
      width: 30,
      headerClassName: "columnHeaders",
      renderCell: ({ row }) => (
        <div className={styles.circle_cell}>
          {selectedFiles.map((sf) => sf.uuid).includes(row.uuid) ? (
            <CircleIcon sx={{ fontSize: "14px" }} />
          ) : (
            <CircleOutlinedIcon sx={{ fontSize: "14px" }} />
          )}
        </div>
      ),
    },
    {
      field: "type",
      headerName: "",
      width: 50,
      headerClassName: "columnHeaders",
      renderCell: ({ row }) => (
        <div>
          {row.type === "folder" || row.type === "site_folder" || row.type === "subject_folder" ? (
            <FolderIcon sx={{ fontSize: "20px", marginTop: "4px" }} />
          ) : (
            <InsertDriveFileOutlinedIcon sx={{ fontSize: "20px", marginTop: "4px" }} />
          )}
        </div>
      ),
    },
    ,
    {
      field: "name",
      headerName: "Name",
      width: 400,
      headerClassName: "columnHeaders",
      renderCell: ({ row }) => (
        <div>
          <span
            className={styles.folder_link}
            onClick={() => {
              if (row.type === "folder" || row.type === "site_folder" || row.type === "subject_folder") {
                fetch_path({
                  name: row.name,
                  uuid: row.uuid,
                });
              }
            }}
          >
            {" "}
            {row.name}
          </span>
        </div>
      ),
    },
  ];
  if (parent === "corelab") {
    columns = columns.concat([
      {
        field: "site_name",
        headerName: "Site",
        type: "text",
        width: 200,
        align: "center",
        headerAlign: "center",
        headerClassName: "columnHeaders",
      },
    ]);
  }
  if (viewMode === "file") {
    columns = columns.concat([
      {
        field: "modified_on",
        headerName: "Modified",
        type: "number",
        width: 150,
        align: "center",
        headerAlign: "center",
        headerClassName: "columnHeaders",
        renderCell: ({ row }) => <div>{moment(row.modified_on).format("MMMM DD, YYYY")}</div>,
      },
      {
        field: "size",
        headerName: "Size (MB)",
        type: "number",
        width: 150,
        align: "center",
        headerAlign: "center",
        headerClassName: "columnHeaders",
        renderCell: ({ row }) => (
          <div>{(row.type === "file" && (Math.round(row.size * 10 ** 2) / 10 ** 2).toFixed(2)) || ""}</div>
        ),
      },
      {
        field: "flags",
        headerName: "Flags",
        type: "text",
        minWidth: 400,
        align: "center",
        headerAlign: "center",
        headerClassName: "columnHeaders",
        renderCell: ({ row }) => (
          <div className={styles.tag_wrapper}>
            {row.flags?.map((f) => (
              <Tag text={f.toUpperCase()} />
            ))}
          </div>
        ),
      },
    ]);
  }
  if (viewMode === "folder") {
    columns = columns.concat([
      {
        field: "flags",
        headerName: "Flags",
        type: "number",
        width: 400,
        align: "center",
        headerAlign: "center",
        headerClassName: "columnHeaders",
        renderCell: ({ row }) => {
          return (
            <div className={styles.tag_wrapper}>
              {["file", "subject_folder"].includes(row.type)
                ? row.flags?.map((f) => <Tag text={f.toUpperCase()} />)
                : "-"}
            </div>
          );
        },
      },
    ]);
  }

  return (
    <div className={styles.wrapper}>
      <input
        key={Date.now()}
        ref={file_select_ref}
        type="file"
        accept=".zip,application/zip"
        onChange={(event) => {
          if (event.target.files.length) {
            let extension = Array.from(event.target.files)
              .map((f) => f.name)[0]
              .split(".")
              .slice(-1)[0];
            if (file_queue.find((f) => f.name === event.target.files[0].name && f.percentage !== 100)) {
              return;
            } else if (
              arrayHaveCommonItems(
                Array.from(event.target.files).map((f) => f.name),
                data.filter((d) => d.type === "file").map((d) => d.name)
              )
            ) {
              setReplaceModalIsOpen(event);
            } else if (
              !extension ||
              extension.length < 2 ||
              extension.length > 5 ||
              extension !== "zip" ||
              event.target.files[0].type !== "application/zip"
            ) {
              setInvalidExtensionModalIsOpen(Array.from(event.target.files)[0].name);
              //checking for an invalid filename
            } else {
              handleFileAddedToQueue(dispatch, event, location.slice(-1)[0].uuid, subject_id);
            }
            // event.target.value = null;
          }
        }}
        style={{ display: "none" }}
      />
      <div className={styles.table_wrapper}>
        <div className={styles.table_header}>
          <BreadCrumbs location={location} fetch_path={fetch_path} />
          <SelectedFileButtons
            selectedFiles={selectedFiles}
            initDelete={initDelete}
            setRenameModalIsOpen={setRenameModalIsOpen}
            from_module_bar={from_module_bar}
            showFlagsModal={() => setShowFlagsModal(true)}
          />

          <div className={styles.right_button_wrapper}>
            {location.slice(-1)[0]?.type !== "root" && !from_module_bar ? (
              <Button
                className={styles.button}
                onClick={() => setAddFolderIsOpen(true)}
                sx={{
                  justifyContent: "center",
                }}
              >
                <AddIcon
                  sx={{
                    fontSize: "18px",
                    marginBottom: "2px",
                    marginRight: "2px",
                  }}
                />
                Folder
              </Button>
            ) : null}
            {location.slice(-1)[0]?.type !== "root" && !from_module_bar ? (
              <Button
                className={styles.button}
                onClick={() => {
                  //this used to call handleFileChange
                  file_select_ref.current.click();
                }}
              >
                <FileUploadIcon
                  sx={{
                    fontSize: "18px",
                    marginBottom: "0px",
                    marginRight: "2px",
                  }}
                />
                Upload
              </Button>
            ) : null}
          </div>
          {from_module_bar && (
            <div>
              <FileFolderToggle onClick={() => toggleViewMode("file")} mode={"folder"} />
            </div>
          )}
        </div>
        <div
          style={{
            height: from_module_bar ? "calc(100vh - 200px)" : "calc(100vh - 300px)",
          }}
        >
          <ThemeProvider theme={theme}>
            <DataGrid
              rows={data}
              columns={columns}
              disableColumnSelector
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooterSelectedRowCount
              columnHeaderHeight={34}
              rowHeight={34}
              autoPageSize
              sx={{
                boxShadow: 0,
                border: 1,
                borderColor: "#f0f0f0",
                "& .MuiDataGrid-cell:hover": {
                  color: "#8349fd",
                  cursor: "pointer",
                },
                "& .MuiDataGrid-topContainer .MuiDataGrid-columnHeader ": {
                  // backgroundColor: "#f7f3fe",
                  backgroundColor: colors.table_header_background,
                },
                "& .MuiDataGrid-topContainer .MuiDataGrid-filler ": {
                  // backgroundColor: "#f7f3fe",
                  backgroundColor: colors.table_header_background,
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                  color: "#333",
                },
              }}
              onRowClick={(row) => {
                // if (row.row.type === "folder") {
                //   fetch_path({ name: row.row.name, uuid: row.row.uuid });
                // }
                toggleFileSelection(row.row.uuid, row.row.type, row.row.name, row.row.aws_key, row.row.flags);
              }}
              onCellClick={(cell) => {
                // if (cell.field === "name" && cell.row.type === "folder") {
                //   fetch_path({ name: cell.row.name, uuid: cell.row.uuid });
                // }
              }}
              hideFooterPagination
              hideFooterSelectedRowCount
              hideFooter
              slots={{
                noRowsOverlay: () => <div className={styles.no_rows_overlay}>Empty Folder</div>,
              }}
              loading={data === null}
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
            />
          </ThemeProvider>
        </div>
      </div>
      <NewFolderModal
        isOpen={addFolderIsOpen}
        close={() => setAddFolderIsOpen(false)}
        onYes={createNewFolder}
        data={data}
      />
      <DeleteAssetModal
        isOpen={deleteAssetIsOpen}
        close={() => setDeleteAssetIsOpen(false)}
        onYes={deleteAssets}
        selectedFiles={selectedFiles}
      />
      <ContainsDataNoDeleteModal
        isOpen={containsDataIsOpen}
        close={() => setContainsDataIsOpen(false)}
        selectedFiles={selectedFiles}
      />

      <ReplaceModal
        isOpen={replaceModalIsOpen}
        close={() => setReplaceModalIsOpen(false)}
        onYes={() => {
          let event = replaceModalIsOpen; //we stored it in the state
          handleFileAddedToQueue(dispatch, replaceModalIsOpen, get_current_location_uuid(), subject_id, true);
          setReplaceModalIsOpen(false);
          event.target.value = null;
        }}
      />
      <RenameModal
        data={selectedFiles}
        allFilesList={data}
        isOpen={renameModalIsOpen}
        close={() => setRenameModalIsOpen(false)}
        onYes={(re_fetch, new_name) => {
          setRenameModalIsOpen(false);
          if (re_fetch) {
            fetch_file_list();
            let _selectedFiles = [...selectedFiles];
            _selectedFiles[0].name = new_name;
            setSelectedFiles(_selectedFiles);
          }
        }}
      />
      <InvalidExtensionModal
        openData={invalidExtensionModalIsOpen}
        close={() => setInvalidExtensionModalIsOpen(false)}
      />
      <FlagsModal
        selectedFile={selectedFiles[0] || {}}
        isOpen={showFlagsModal}
        close={() => setShowFlagsModal(false)}
        handleModifyFlags={handleModifyFlags}
      />
    </div>
  );
}
